import { useState, useEffect, FormEvent } from "react";
import api from "@/services/api";

interface Client {
  id: string;
  company_name: string;
}

interface InvoiceItem {
  label: string;
  description?: string;
  quantity: number;
  unit_amount: number;
  amount: number;
}

interface InvoiceFormState {
  client_id: string;
  number: string;
  description: string;
  due_date: string;
  items: InvoiceItem[];
}

type Mode = "create" | "edit";

interface InvoiceModalProps {
  open: boolean;
  mode: Mode;
  initial?: Partial<InvoiceFormState>;
  onClose: () => void;
  onSubmit: (data: InvoiceFormState) => Promise<void>;
}

export default function InvoiceModal({ open, mode, initial, onClose, onSubmit }: InvoiceModalProps) {
  const [clients, setClients] = useState<Client[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [form, setForm] = useState<InvoiceFormState>({
    client_id: "",
    number: "",
    description: "",
    due_date: "",
    items: [{ label: "", description: "", quantity: 1, unit_amount: 0, amount: 0 }],
  });

  useEffect(() => {
    if (open) {
      api.get("/clients").then((res) => setClients(res.data.data || []));
    }
  }, [open]);

  useEffect(() => {
    if (open && initial) {
      setForm({
        client_id: initial.client_id || "",
        number: initial.number || "",
        description: initial.description || "",
        due_date: initial.due_date || "",
        items: initial.items?.length ? initial.items : [{ label: "", description: "", quantity: 1, unit_amount: 0, amount: 0 }],
      });
    } else if (open && !initial) {
      setForm({
        client_id: clients[0]?.id || "",
        number: `INV-${Date.now()}`,
        description: "",
        due_date: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], // 30 days from now
        items: [{ label: "", description: "", quantity: 1, unit_amount: 0, amount: 0 }],
      });
    }
  }, [open, initial, clients]);

  if (!open) return null;

  const calculateAmount = (quantity: number, unit_amount: number) => quantity * unit_amount;

  const updateItem = (index: number, field: keyof InvoiceItem, value: string | number) => {
    const newItems = [...form.items];
    newItems[index] = { ...newItems[index], [field]: value };
    if (field === 'quantity' || field === 'unit_amount') {
      newItems[index].amount = calculateAmount(newItems[index].quantity, newItems[index].unit_amount);
    }
    setForm({ ...form, items: newItems });
  };

  const addItem = () => {
    setForm({
      ...form,
      items: [...form.items, { label: "", description: "", quantity: 1, unit_amount: 0, amount: 0 }],
    });
  };

  const removeItem = (index: number) => {
    setForm({ ...form, items: form.items.filter((_, i) => i !== index) });
  };

  const totalAmount = form.items.reduce((sum, item) => sum + item.amount, 0);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    const payload: any = {
      client_id: form.client_id,
      number: form.number,
      description: form.description,
      due_date: form.due_date,
      amount: totalAmount,
      items: form.items.filter(item => item.label.trim()),
    };

    try {
      await onSubmit(payload);
      onClose();
    } catch (err: any) {
      setError(err instanceof Error ? err.message : "Failed to save invoice");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />

      <div className="relative bg-dark-panel border border-dark-border rounded-2xl p-6 max-w-4xl w-full max-h-[90vh] overflow-y-auto animate-slide-up">
        <div className="flex items-start justify-between gap-4 mb-6">
          <div>
            <h3 className="text-white font-bold text-lg" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
              {mode === "create" ? "Create Invoice" : "Edit Invoice"}
            </h3>
            <p className="text-silver text-sm mt-1">Generate a professional invoice for your client.</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="w-9 h-9 flex items-center justify-center rounded-lg border border-dark-border text-silver-dim hover:text-white hover:border-electric-blue/40 transition-colors cursor-pointer"
            aria-label="Close"
          >
            <i className="ri-close-line" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {error && <p className="text-red-500 text-sm">{error}</p>}
          {/* Basic Info */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <label className="block">
              <span className="text-silver-dim text-xs font-semibold uppercase tracking-wider">Client</span>
              <select
                className="mt-1 w-full bg-dark-card border border-dark-border rounded-xl px-3 py-2.5 text-white text-sm focus:outline-none focus:border-electric-blue/50 transition-colors disabled:opacity-50"
                value={form.client_id}
                onChange={(e) => setForm({ ...form, client_id: e.target.value })}
                required
                disabled={mode === "edit"}
              >
                <option value="" disabled>Select client</option>
                {clients.map((client) => (
                  <option key={client.id} value={client.id}>{client.company_name}</option>
                ))}
              </select>
            </label>

            <label className="block">
              <span className="text-silver-dim text-xs font-semibold uppercase tracking-wider">Invoice Number</span>
              <input
                className="mt-1 w-full bg-dark-card border border-dark-border rounded-xl px-3 py-2.5 text-white text-sm focus:outline-none focus:border-electric-blue/50 transition-colors disabled:opacity-50"
                value={form.number}
                onChange={(e) => setForm({ ...form, number: e.target.value })}
                required
                placeholder="INV-001"
                disabled={mode === "edit"}
              />
            </label>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <label className="block">
              <span className="text-silver-dim text-xs font-semibold uppercase tracking-wider">Description</span>
              <input
                className="mt-1 w-full bg-dark-card border border-dark-border rounded-xl px-3 py-2.5 text-white text-sm focus:outline-none focus:border-electric-blue/50 transition-colors"
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                placeholder="Invoice description"
              />
            </label>

            <label className="block">
              <span className="text-silver-dim text-xs font-semibold uppercase tracking-wider">Due Date</span>
              <input
                type="date"
                className="mt-1 w-full bg-dark-card border border-dark-border rounded-xl px-3 py-2.5 text-white text-sm focus:outline-none focus:border-electric-blue/50 transition-colors"
                value={form.due_date}
                onChange={(e) => setForm({ ...form, due_date: e.target.value })}
                required
              />
            </label>
          </div>

          {/* Invoice Items */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h4 className="text-white font-semibold text-sm uppercase tracking-wider">Invoice Items</h4>
              <button
                type="button"
                onClick={addItem}
                className="flex items-center gap-2 bg-dark-card border border-dark-border text-silver hover:text-white hover:border-electric-blue/40 px-3 py-1.5 rounded-lg text-xs transition-colors cursor-pointer"
              >
                <i className="ri-add-line" />
                Add Item
              </button>
            </div>

            {form.items.map((item, index) => (
              <div key={index} className="grid grid-cols-1 md:grid-cols-12 gap-3 p-4 bg-dark-card border border-dark-border rounded-xl">
                <div className="md:col-span-3">
                  <label className="block text-silver-dim text-xs font-medium mb-1">Item</label>
                  <input
                    className="w-full bg-deep-black border border-dark-border rounded-lg px-2 py-1.5 text-white text-xs focus:outline-none focus:border-electric-blue/50"
                    value={item.label}
                    onChange={(e) => updateItem(index, 'label', e.target.value)}
                    placeholder="Item name"
                  />
                </div>
                <div className="md:col-span-3">
                  <label className="block text-silver-dim text-xs font-medium mb-1">Description</label>
                  <input
                    className="w-full bg-deep-black border border-dark-border rounded-lg px-2 py-1.5 text-white text-xs focus:outline-none focus:border-electric-blue/50"
                    value={item.description || ""}
                    onChange={(e) => updateItem(index, 'description', e.target.value)}
                    placeholder="Optional description"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-silver-dim text-xs font-medium mb-1">Qty</label>
                  <input
                    type="number"
                    min="1"
                    className="w-full bg-deep-black border border-dark-border rounded-lg px-2 py-1.5 text-white text-xs focus:outline-none focus:border-electric-blue/50"
                    value={item.quantity}
                    onChange={(e) => updateItem(index, 'quantity', parseInt(e.target.value) || 1)}
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-silver-dim text-xs font-medium mb-1">Unit Price</label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    className="w-full bg-deep-black border border-dark-border rounded-lg px-2 py-1.5 text-white text-xs focus:outline-none focus:border-electric-blue/50"
                    value={item.unit_amount}
                    onChange={(e) => updateItem(index, 'unit_amount', parseFloat(e.target.value) || 0)}
                  />
                </div>
                <div className="md:col-span-1">
                  <label className="block text-silver-dim text-xs font-medium mb-1">Total</label>
                  <div className="w-full bg-deep-black border border-dark-border rounded-lg px-2 py-1.5 text-electric-blue text-xs font-semibold">
                    ${item.amount.toLocaleString()} FCFA
                  </div>
                </div>
                <div className="md:col-span-1 flex items-end">
                  {form.items.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeItem(index)}
                      className="w-7 h-7 flex items-center justify-center rounded-lg border border-cyber-red/30 text-cyber-red hover:bg-cyber-red/10 cursor-pointer"
                    >
                      <i className="ri-delete-bin-line text-xs" />
                    </button>
                  )}
                </div>
              </div>
            ))}

            <div className="flex justify-end">
              <div className="bg-dark-card border border-dark-border rounded-xl px-4 py-3">
                <span className="text-silver-dim text-sm">Total: </span>
                <span className="text-white font-bold text-lg">{totalAmount.toLocaleString()} FCFA</span>
              </div>
            </div>
          </div>

          <div className="flex gap-3 pt-1">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 py-2.5 rounded-xl border border-dark-border text-silver hover:text-white hover:border-electric-blue/40 transition-all text-sm font-medium cursor-pointer"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className={`flex-1 py-2.5 rounded-xl font-semibold text-sm transition-all cursor-pointer whitespace-nowrap ${
                loading ? "bg-electric-blue/40 text-deep-black cursor-not-allowed" : "bg-electric-blue text-deep-black hover:shadow-glow-blue"
              }`}
            >
              {loading ? "Creating..." : mode === "create" ? "Create Invoice" : "Update Invoice"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}